// Storage durability. On kiosk devices the browser stores our IndexedDB
// (device token, downloaded media, queued logs) as "best-effort", which Chromium
// is free to evict wholesale under disk/quota pressure. When that happens the
// whole origin is cleared at once: the device appears logged out with no
// content. Requesting persistent storage opts the origin out of automatic
// eviction so only an explicit reset can clear it.

export interface StorageStatus {
  persisted: boolean;
  usage?: number;
  quota?: number;
}

function fmtBytes(n?: number): string {
  if (n == null) return 'n/a';
  const mb = n / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GiB` : `${mb.toFixed(1)} MiB`;
}

export async function getStorageEstimate(): Promise<StorageStatus> {
  const status: StorageStatus = { persisted: false };
  try {
    if (navigator.storage?.persisted) {
      status.persisted = await navigator.storage.persisted();
    }
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      status.usage = est.usage;
      status.quota = est.quota;
    }
  } catch {
    // Storage Manager API unavailable.
  }
  return status;
}

// Request persistent storage and report the result. Safe to call repeatedly;
// once granted the browser remembers it. Fire-and-forget on boot.
export async function ensurePersistentStorage(): Promise<StorageStatus> {
  const status: StorageStatus = { persisted: false };
  try {
    if (navigator.storage?.persisted) {
      status.persisted = await navigator.storage.persisted();
    }
    if (!status.persisted && navigator.storage?.persist) {
      status.persisted = await navigator.storage.persist();
    }
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      status.usage = est.usage;
      status.quota = est.quota;
    }
  } catch {
    // Storage Manager API unavailable; nothing we can do here.
  }

  const pct =
    status.usage != null && status.quota
      ? ` (${((status.usage / status.quota) * 100).toFixed(1)}% of quota)`
      : '';
  console.info(
    `[storage] persisted=${status.persisted} usage=${fmtBytes(status.usage)} quota=${fmtBytes(status.quota)}${pct}`,
  );
  if (!status.persisted) {
    console.warn(
      '[storage] persistent storage NOT granted — IndexedDB can be evicted under disk pressure',
    );
  }
  return status;
}
